import { Component, EventEmitter, forwardRef, inject, Input, Output } from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'
import { IonicModule } from '@ionic/angular'
import { AsyncPipe, NgClass } from '@angular/common'
import { BehaviorSubject, combineLatest, Observable } from 'rxjs'
import { AssistanceStatus, AssistanceStatusName } from '../../../../models/assistanceStatus'
import { AssistanceStatusesService } from '../../../../services/domain/codelists/assistance-statuses.service'
import { map } from 'rxjs/operators'
import { Assistance } from '../../../../models/assistance'
import { FormStatus } from '../../../../models/functional/formStatus'
import { EditorAssistance } from '../../../../models/EditorAssistance'
import { FormStatusType } from '../../../../models/functional/formStatusType'

@Component({
  selector: 'app-assistance-editor-assistance-status',
  templateUrl: './assistance-editor-assistance-status.component.html',
  styleUrls: ['./assistance-editor-assistance-status.component.scss'],
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AssistanceEditorAssistanceStatusComponent),
      multi: true,
    },
  ],
  imports: [IonicModule, AsyncPipe, NgClass],
})
export class AssistanceEditorAssistanceStatusComponent implements ControlValueAccessor {
  private readonly statusService = inject(AssistanceStatusesService)

  @Input() editable: boolean = false
  @Input() formStatus: FormStatus
  @Input() statusValidationInputs: { realizedFrom: Date; realizedTo: Date }
  @Input() assistance: EditorAssistance

  @Output() advanceStatusRequested = new EventEmitter<void>()
  @Output() cancelAssistanceRequested = new EventEmitter<void>()

  private _value: number | null = null // statusId
  public disabled: boolean
  private _onChange: (value: number) => void
  protected _onTouched: () => void
  protected readonly AssistanceStatusName = AssistanceStatusName

  private _isStatusChangeAllowedCache: boolean | null = null
  private _lastCacheKey: string | null = null

  valueChanged$$ = new BehaviorSubject<void>(null)
  valueChanged$ = this.valueChanged$$.asObservable()

  protected currentStatus$: Observable<AssistanceStatus> = combineLatest([
    this.statusService.getAll(),
    this.valueChanged$,
  ]).pipe(
    map(([statuses, _]) => {
      if (!this._value) {
        const defaultStatus = statuses.find((s) => s.name === AssistanceStatusName.PLANNED)
        if (defaultStatus) {
          this._value = defaultStatus.id
          this._onChange(this._value)
          return defaultStatus
        }
      }
      return statuses.find((s) => s.id === this._value)
    }),
  )

  protected positiveStatuses$: Observable<AssistanceStatus[]> = this.statusService.getPositiveStatusesOrdered()

  registerOnChange(fn: (value: number) => void): void {
    this._onChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled
  }

  writeValue(value: number | null): void {
    this._value = value ?? null
    this.valueChanged$$.next()
  }

  canShowStatusButtonCancel(status: AssistanceStatus): boolean {
    return Assistance.canBeCanceled(
      this.statusValidationInputs?.realizedFrom,
      this.statusValidationInputs?.realizedTo,
      status,
    )
  }

  /**
   * Checks that the status change is allowed for the assistance.
   * Uses memoization to optimize performance during repeated calls in change detection.
   */
  isStatusChangeAllowed(): boolean {
    if (!this.assistance) {
      return false
    }

    const cacheKey = this.getCacheKey()

    if (this._lastCacheKey !== cacheKey) {
      this._lastCacheKey = cacheKey
      this._isStatusChangeAllowedCache = this.calculateStatusChangeAllowed()
    }

    return this._isStatusChangeAllowedCache ?? false
  }

  /**
   * Calculates whether status change is allowed based on current form state.
   * Buttons are only shown for existing assistances, so we only check form state.
   */
  private calculateStatusChangeAllowed(): boolean {
    // For existing assistances: allow status change if form is settled (NO_CHANGES/SAVED)
    // or has valid unsaved changes (will be auto-saved before status change)
    const isFormSettled = this.formStatus?.isSettled() ?? false
    const hasValidUnsavedChanges = this.formStatus?.statusType === FormStatusType.VALID_UNSAVED_CHANGES

    return isFormSettled || hasValidUnsavedChanges
  }

  /**
   * Generates unique cache key for memoization based on relevant values.
   * Cache invalidates when formStatus, assistance.id or isSavedInDatabase changes.
   */
  private getCacheKey(): string {
    const formStatusType = this.formStatus?.statusType ?? 'null'
    const assistanceId = this.assistance?.id ?? 'null'
    const isSavedInDatabase = this.assistance?.isSavedInDatabase() ? 'true' : 'false'

    return `${formStatusType}-${assistanceId}-${isSavedInDatabase}`
  }

  async advanceStatus(): Promise<void> {
    this.advanceStatusRequested.emit()
  }

  async requestCancel(): Promise<void> {
    this.cancelAssistanceRequested.emit()
  }
}
